import React from 'react';
import {Grid, Header} from 'semantic-ui-react';
import _ from 'lodash';
import {
  requestDetailsChangesToDisplayString,
  diffItem,
  displayError,
  createChangeObj,
  calculatePeriod,
} from 'common/requestDetails/utils';
import {produce} from 'immer';
import {TextAreaField} from 'app-ui-componentlibrary/dist/go/components/common/FormControls';
import {
  BOC,
  BOC_NAMES,
  DESCRIPTION,
  PRICE,
  QUANTITY,
  CHANGES_TYPES,
  UNIT_OF_MEASURE,
  PRE_AWARD,
  VALUE_CHANGE_FORMAT,
  EXPLAIN_OTHER,
  NAME,
  VALUE_CHANGE_DISPLAY,
  PHASED_PROJECT,
  ANNUAL_BENEFITS,
  ANNUAL_SALARY,
  CARD_TYPES,
  ADDITIONAL_INFORMATION,
  PERSONNEL,
  FRINGE_BENEFITS,
  COST,
} from 'common/requestDetails/constants';
import pluralize from 'pluralize';
import SimpleShared from '../shared/SimpleShared';
export default class SimpleHiring extends React.Component {
  state = {
    lineItem: this.props.item,
    editMode: false,
  };

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  handleChange = (lineItem) => {
    return new Promise((resolve) => {
      this.setState({lineItem}, () => resolve());
    });
  };

  toggleEditModeIneligibleDeferred = (value, editor) => {
    if (value && !editor) {
      this.toggleEditMode();
    }
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;
      lineItem = _.cloneDeep(lineItem);
      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  handleItemChange = (name, value, ind = -1) => {
    let attributeName = _.split(name, '.').pop();
    if (attributeName === QUANTITY) {
      return this.handleBundledItemChange(attributeName, value);
    } else if (
      this.props.costType == CARD_TYPES.STAFFING_BUNDLED &&
      attributeName === PRICE
    ) {
      return this.handleBundledItemChange(attributeName, value, ind);
    } else {
      let newLineItem = _.cloneDeep(this.props.item);
      _.set(newLineItem, name, value);
      newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
      return this.handleChange(newLineItem);
    }
  };

  handleBundledItemChange = (attributeName, value, ind = -1) => {
    let newLineItem = _.cloneDeep(this.props.item);
    let costs = _.get(newLineItem, COST);
    _.map(costs, (costArray) => {
      if (ind !== -1) {
        _.set(costArray[ind], attributeName, value);
      } else {
        _.map(costArray, (cost) => {
          _.set(cost, attributeName, value);
        });
      }
    });
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleIneligibleItemChange = (name, value, ind = -1) => {
    let oldItem = this.props.originalItem;
    let newItem = this.props.item;
    let justification = _.get(newItem, 'additionalInformation.justification');
    let newLineItem = oldItem ? _.cloneDeep(oldItem) : _.cloneDeep(newItem);
    _.set(newLineItem, name, value);
    // save current justification to prevent incorrect validation errors
    if (!!oldItem && justification) {
      _.set(newLineItem, 'additionalInformation.justification', justification);
    }
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    return this.handleChange(newLineItem);
  };

  handleIneligibleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleIneligibleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
    // if marking ineligible, should disable edit mode as only change should be that it's marked ineligible
    this.toggleEditModeIneligibleDeferred(value, !this.state.editMode);
  };

  handleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndEditMode = (name, value, fieldName) => {
    this.handleItemChangeAndBlur(name, value, fieldName);
    this.toggleEditModeIneligibleDeferred(value, this.state.editMode);
  };

  getItemWithChanges = (oldItem, newItemIn) => {
    const {itemConfig, costType} = this.props;
    const period = calculatePeriod(itemConfig);
    const isStaffingBundled = costType === CARD_TYPES.STAFFING_BUNDLED;

    let displayCostRow = _.get(oldItem, [COST, 0], []).length > 1;
    let newItem = produce(newItemIn, (draft) => {
      return diffItem(
        draft,
        oldItem,
        _.get(itemConfig, 'labels'),
        this.props.itemCodeOptions
      );
    });

    let descriptionLabel = _.get(itemConfig, 'labels.description');
    let otherItemLabel = _.get(itemConfig, 'labels.otherDescription');
    let fullTimePositionLabel = _.get(itemConfig, 'labels.position');
    let changes = [];
    let descriptionPath = 'additionalInformation.description';

    if (oldItem) {
      const findCost = (item, year, ind) => {
        return _.get(item, [COST, year, ind], {});
      };

      if (!_.isEqual(_.get(oldItem, descriptionPath), _.get(newItem, descriptionPath))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE_NO_DISPLAY,
            DESCRIPTION,
            undefined,
            undefined,
            descriptionLabel
          )
        );
      }

      if (!_.isEqual(_.get(oldItem, EXPLAIN_OTHER), _.get(newItem, EXPLAIN_OTHER))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE,
            NAME,
            _.get(oldItem, EXPLAIN_OTHER),
            _.get(newItem, EXPLAIN_OTHER),
            otherItemLabel
          )
        );
      }

      let quantityChanged = false;
      let salaryChanged = false;
      let benefitsChanged = false;

      _.forEach(_.get(newItem, COST, []), (phaseCost, phaseInd) => {
        _.forEach(phaseCost, (newCost, ind) => {
          const oldCost = findCost(oldItem, phaseInd, ind);
          const quantity = _.get(newCost, QUANTITY);
          const price = _.get(newCost, PRICE);
          const quantityOld = _.get(oldCost, QUANTITY);
          const priceOld = _.get(oldCost, PRICE);
          const added = _.get(newCost, 'added', false);
          let costRow = displayCostRow ? _.get(newCost, 'costRow', 0) : 0;
          if (added) {
            changes.push({
              ...createChangeObj(
                CHANGES_TYPES.NEWLY_CREATED,
                undefined,
                undefined,
                undefined,
                undefined
              ),
              costRow: costRow,
              periodType: period.type,
            });
          } else {
            if (_.toNumber(quantity) !== _.toNumber(quantityOld) && !quantityChanged) {
              let message = `cost ${ind + 1} ${_.upperFirst(period.type)} ${
                phaseInd + 1
              } ${QUANTITY}`;
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  fullTimePositionLabel,
                  quantityOld,
                  quantity,
                  `Number of ${pluralize(fullTimePositionLabel)}`
                ),
                costRow: costRow,
                periodType: period.type,
              });
              quantityChanged = true;
            }
            if (_.toNumber(price) !== _.toNumber(priceOld)) {
              let priceName = PRICE;
              let boc = _.get(newCost, BOC);
              if (
                !isStaffingBundled ||
                (boc === PERSONNEL && !salaryChanged) ||
                (boc === FRINGE_BENEFITS && !benefitsChanged)
              ) {
                switch (boc) {
                  case PERSONNEL:
                    priceName = ANNUAL_SALARY;
                    salaryChanged = true;
                    break;
                  case FRINGE_BENEFITS:
                    priceName = ANNUAL_BENEFITS;
                    benefitsChanged = true;
                    break;
                  default:
                    break;
                }
                let periodDisplayText = !isStaffingBundled
                  ? `${_.upperFirst(period.type)} ${phaseInd + 1}`
                  : '';
                let message = `${periodDisplayText} ${VALUE_CHANGE_DISPLAY[priceName]}`;
                changes.push({
                  ...createChangeObj(
                    CHANGES_TYPES.VALUE_CHANGE,
                    PRICE,
                    priceOld,
                    price,
                    period.type !== 'whole' ? message : VALUE_CHANGE_DISPLAY[PRICE]
                  ),
                  costRow: costRow,
                  periodType: period.type,
                });
              }
            }

            let boc = _.get(newCost, BOC);
            let bocOld = _.get(oldCost, BOC);

            if (boc !== bocOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  BOC,
                  BOC_NAMES[bocOld],
                  BOC_NAMES[boc]
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
            const unitOfMeasure = _.get(newCost, 'unitOfMeasure');
            const unitOfMeasureOld = _.get(oldCost, 'unitOfMeasure');

            if (unitOfMeasure !== unitOfMeasureOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  UNIT_OF_MEASURE,
                  unitOfMeasureOld,
                  unitOfMeasure
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }

            const preAward = _.get(newCost, 'preAward', false);
            const preAwardOld = _.get(oldCost, 'preAward', false);

            if (preAward !== preAwardOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PRE_AWARD,
                  preAwardOld,
                  preAward
                ),
                costRow: costRow,
                periodType: period.type,
                formatType: VALUE_CHANGE_FORMAT.preAward,
              });
            }

            const phasedProject = _.get(newCost, 'phasedProject', false);
            const phasedProjectOld = _.get(oldCost, 'phasedProject', false);

            if (phasedProject !== phasedProjectOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PHASED_PROJECT,
                  phasedProjectOld,
                  phasedProject
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
          }
        });
      });
    }

    let updatedItem = produce(newItem, (draft) =>
      _.set(
        draft,
        ['additionalInformation', 'changes'],
        _.concat(_.get(newItem, 'additionalInformation.changes'), changes)
      )
    );

    return updatedItem;
  };

  renderChangesSection = () => {
    const {lineItem, editMode} = this.state;
    const {
      validationErrors,
      validationDirtyMap,
      changeLabel,
      justificationLabel,
      editable,
    } = this.props;
    const justification = _.get(lineItem, 'additionalInformation.justification');
    const changes = _.get(lineItem, 'additionalInformation.changes');
    const formattedSortedChanges = _.map(changes, (change) =>
      requestDetailsChangesToDisplayString(change)
    );
    const JUSTIFICATION = 'additionalInformation.justification';

    const justificationErrors = displayError(
      'justification',
      JUSTIFICATION,
      validationErrors,
      validationDirtyMap,
      false
    );

    const newlyCreated = _.find(
      _.get(lineItem, 'additionalInformation.changes', []),
      (change) => change.type === 'newlyCreated'
    );

    const ineligible = _.get(lineItem, 'additionalInformation.ineligible');

    return (
      <Grid className="diffingBackground change-section-grid">
        <Grid.Row>
          <Grid.Column computer={8} mobile={16}>
            <Header as="h5">{changeLabel ? changeLabel : 'Reviewer Edits'}</Header>
            <div>
              {_.map(formattedSortedChanges, (change, index) => {
                return <p key={index}>{change}</p>;
              })}
            </div>
          </Grid.Column>
          <Grid.Column computer={8} mobile={16}>
            {(editMode || newlyCreated || ineligible) && editable ? (
              <TextAreaField
                style={{width: '100%'}}
                value={justification}
                title={
                  justificationLabel
                    ? justificationLabel
                    : 'Justification (included in award package)'
                }
                label={
                  justificationLabel
                    ? justificationLabel
                    : 'Justification (included in award package)'
                }
                id={`${JUSTIFICATION}-${this.props.id}`}
                name={JUSTIFICATION}
                handleChange={(e, data) => {
                  const newLineItem = produce(lineItem, (draft) => {
                    _.set(draft, JUSTIFICATION, data.value);
                  });
                  this.handleChange(newLineItem);
                }}
                handleBlur={() => this.handleBlur(JUSTIFICATION)}
                displayError={justificationErrors.displayError}
                errorMessages={justificationErrors.errorMessages}
              />
            ) : (
              <div>
                <Header as="h5">
                  {justificationLabel
                    ? justificationLabel
                    : 'Justification (included in award package)'}
                </Header>
                {justificationErrors.displayError ? (
                  <p className="errorMessages">{justificationErrors.errorMessages}</p>
                ) : (
                  justification
                )}
              </div>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  render() {
    return (
      <SimpleShared
        lineItem={this.state.lineItem}
        editMode={this.state.editMode}
        toggleEditMode={this.toggleEditMode}
        handleItemChangeAndEditMode={this.handleItemChangeAndEditMode}
        renderChangesSection={this.renderChangesSection}
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handleItemChange={this.handleItemChange}
        handleItemChangeAndBlur={this.handleItemChangeAndBlur}
        handleIneligibleItemChange={this.handleIneligibleItemChange}
        handleIneligibleItemChangeAndBlur={this.handleIneligibleItemChangeAndBlur}
        {...this.props}
      />
    );
  }
}
